import { NavLink, useNavigate } from "react-router-dom";
import { Home, Kanban, Lightbulb, Bookmark, Calendar, Target, BarChart3, Settings, LogOut, Plus, Menu, X, Wand2, HelpCircle, CreditCard, TrendingUp, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useState, useEffect } from "react";
import IdeaModal from "@/components/IdeaModal";
import PwaInstallBanner from "@/components/PwaInstallBanner";

const NAV = [
  { to: "/today", icon: Sparkles, label: "Today", num: "i", testid: "nav-today", primary: true },
  { to: "/pipeline", icon: Kanban, label: "Pipeline", num: "ii", testid: "nav-pipeline" },
  { to: "/vault", icon: Lightbulb, label: "Vault", num: "iii", testid: "nav-vault" },
  { to: "/scripts", icon: Wand2, label: "Scripts", num: "iv", testid: "nav-scripts" },
  { to: "/swipe", icon: Bookmark, label: "Swipe", num: "v", testid: "nav-swipe" },
  { to: "/calendar", icon: Calendar, label: "Calendar", num: "vi", testid: "nav-calendar" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", num: "vii", testid: "nav-analytics" },
  { to: "/posts", icon: TrendingUp, label: "Posts", num: "viii", testid: "nav-posts" },
  { to: "/goals", icon: Target, label: "Goals", num: "ix", testid: "nav-goals" },
  { to: "/insights", icon: Home, label: "Insights", num: "x", testid: "nav-insights" },
  { to: "/how-it-works", icon: HelpCircle, label: "How it works", num: "xi", testid: "nav-howitworks" },
  { to: "/pricing", icon: CreditCard, label: "Pricing", num: "xii", testid: "nav-pricing" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showNewIdea, setShowNewIdea] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change (NavLink click)
  useEffect(() => {
    if (!drawerOpen) return;
    const close = () => setDrawerOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, [drawerOpen]);

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "var(--bg)", color: "var(--ink)" }}>
      {/* Desktop side nav */}
      <aside
        className="hidden md:flex fixed top-0 left-0 bottom-0 w-60 flex-col py-6 px-4 z-40"
        style={{ background: "var(--bg)", borderRight: "1px solid var(--ink-22)" }}
      >
        <div className="flex items-center gap-3 px-2 mb-10">
          <span className="seal-dot" />
          <span className="font-heading text-lg" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
            PintaFlow
          </span>
          <span
            className="ml-auto text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
            style={{ background: "rgba(216,168,87,0.10)", border: "1px solid rgba(216,168,87,0.35)", color: "var(--seal)" }}
          >
            Beta
          </span>
        </div>

        <button
          data-testid="sidebar-new-idea"
          onClick={() => setShowNewIdea(true)}
          className="btn-primary w-full mb-8"
        >
          <Plus size={14} /> New Idea
        </button>

        <nav className="flex flex-col gap-0.5 flex-1">
          {NAV.map(({ to, icon: Icon, label, num, testid, primary }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors relative"
              style={({ isActive }) => ({
                color: isActive ? "var(--ink)" : (primary ? "var(--ink)" : "var(--ink-55)"),
                background: isActive ? "var(--surface)" : "transparent",
                boxShadow: primary && !isActive ? "inset 0 0 0 1px rgba(216,168,87,0.22)" : "none",
              })}
            >
              <span
                className="font-mono text-[10px] uppercase tracking-[0.16em] w-6 flex-shrink-0 text-right"
                style={{ color: "var(--seal)", opacity: 0.75 }}
              >
                {num}.
              </span>
              <Icon size={16} strokeWidth={1.6} />
              {label}
              {primary && (
                <span
                  className="ml-auto w-1.5 h-1.5 rounded-full"
                  style={{ background: "var(--seal)", boxShadow: "0 0 8px rgba(216,168,87,0.75)" }}
                />
              )}
            </NavLink>
          ))}
        </nav>

        <div className="pt-4 mt-4 flex flex-col gap-0.5" style={{ borderTop: "1px solid var(--ink-22)" }}>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors"
            style={({ isActive }) => ({
              color: isActive ? "var(--ink)" : "var(--ink-55)",
              background: isActive ? "var(--surface)" : "transparent",
            })}
          >
            <span
              className="font-mono text-[10px] uppercase tracking-[0.16em] w-6 flex-shrink-0 text-right"
              style={{ color: "var(--seal)", opacity: 0.75 }}
            >
              xii.
            </span>
            <Settings size={16} strokeWidth={1.6} /> Settings
          </NavLink>
          {user && (
            <div className="flex items-center gap-2 px-3 py-3 text-xs mt-1" style={{ color: "var(--ink-55)" }}>
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full" style={{ background: "var(--surface-2)" }} />
              )}
              <span className="truncate flex-1" data-testid="sidebar-user-email">{user.email}</span>
              <button
                onClick={logout}
                data-testid="sidebar-logout"
                style={{ color: "var(--ink-55)" }}
                title="Sign out"
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile top bar — hamburger + logo + (+ New) */}
      <header
        className="md:hidden sticky top-0 z-30 backdrop-blur-xl px-4 py-3.5 flex items-center justify-between"
        style={{ background: "rgba(14,8,5,0.94)", borderBottom: "1px solid var(--ink-22)" }}
      >
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-menu-btn"
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "transparent", border: "1px solid var(--ink-22)", color: "var(--ink-72)" }}
          aria-label="Open menu"
        >
          <Menu size={16} />
        </button>
        <div className="flex items-center gap-2.5">
          <span className="seal-dot" />
          <span className="font-heading text-base" style={{ fontVariationSettings: "'opsz' 24, 'wght' 500" }}>
            PintaFlow
          </span>
        </div>
        <button
          onClick={() => setShowNewIdea(true)}
          data-testid="mobile-new-idea"
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: "var(--seal)", color: "var(--bg)" }}
          aria-label="New idea"
        >
          <Plus size={16} strokeWidth={2.2} />
        </button>
      </header>

      {/* Mobile slide-in drawer */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-drawer-backdrop"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-drawer"
            className="absolute top-0 left-0 bottom-0 w-[78%] max-w-[320px] flex flex-col"
            style={{
              background: "var(--bg)",
              borderRight: "1px solid var(--ink-22)",
              animation: "slideInLeft 220ms ease-out",
            }}
          >
            <div className="flex items-center justify-between px-5 py-5" style={{ borderBottom: "1px solid var(--ink-22)" }}>
              <div className="flex items-center gap-3">
                <span className="seal-dot" />
                <span className="font-heading text-lg" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                  PintaFlow
                </span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                data-testid="mobile-drawer-close"
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ color: "var(--ink-72)" }}
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="flex flex-col gap-0.5 flex-1 px-3 py-5 overflow-y-auto">
              {NAV.map(({ to, icon: Icon, label, num, testid, primary }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`mobile-${testid}`}
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors"
                  style={({ isActive }) => ({
                    color: isActive ? "var(--ink)" : (primary ? "var(--ink)" : "var(--ink-72)"),
                    background: isActive ? "var(--surface)" : "transparent",
                    boxShadow: primary && !isActive ? "inset 0 0 0 1px rgba(216,168,87,0.22)" : "none",
                  })}
                >
                  <span
                    className="font-mono text-[11px] uppercase tracking-[0.16em] w-7 flex-shrink-0 text-right"
                    style={{ color: "var(--seal)", opacity: 0.75 }}
                  >
                    {num}.
                  </span>
                  <Icon size={18} strokeWidth={1.6} />
                  {label}
                  {primary && (
                    <span
                      className="ml-auto w-1.5 h-1.5 rounded-full"
                      style={{ background: "var(--seal)", boxShadow: "0 0 8px rgba(216,168,87,0.75)" }}
                    />
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="px-3 pb-6 pt-3 flex flex-col gap-0.5" style={{ borderTop: "1px solid var(--ink-22)" }}>
              <NavLink
                to="/settings"
                data-testid="mobile-nav-settings"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-base transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? "var(--ink)" : "var(--ink-72)",
                  background: isActive ? "var(--surface)" : "transparent",
                })}
              >
                <span
                  className="font-mono text-[11px] uppercase tracking-[0.16em] w-7 flex-shrink-0 text-right"
                  style={{ color: "var(--seal)", opacity: 0.75 }}
                >
                  xii.
                </span>
                <Settings size={18} strokeWidth={1.6} /> Settings
              </NavLink>
              {user && (
                <div className="flex items-center gap-2 px-3 py-3 text-xs mt-1" style={{ color: "var(--ink-55)" }}>
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full" style={{ background: "var(--surface-2)" }} />
                  )}
                  <span className="truncate flex-1" data-testid="mobile-user-email">{user.email}</span>
                  <button
                    onClick={() => { setDrawerOpen(false); logout(); }}
                    data-testid="mobile-logout"
                    style={{ color: "#c97a6a" }}
                    title="Sign out"
                  >
                    <LogOut size={14} />
                  </button>
                </div>
              )}
            </div>
          </aside>
          <style>{`@keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
        </div>
      )}

      {/* Page content */}
      <main
        className="md:ml-60 min-h-screen md:pb-16 px-4 sm:px-5 md:px-10 pt-6 md:pt-10 overflow-x-hidden"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
      >
        {children}
      </main>

      {showNewIdea && (
        <IdeaModal onClose={() => setShowNewIdea(false)} onSaved={() => setShowNewIdea(false)} />
      )}
      <PwaInstallBanner />
    </div>
  );
}
